import { useEffect, useMemo } from "react"

import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { DateTime } from "luxon"

import { TicketCard } from "pages/select-tickets/ticket-card"
import { SelectTicketsPlaceholder } from "pages/select-tickets/select-tickets-placeholder"

import { useNavigateWithParams } from "hooks/navigation/use-navigate-with-params"
import { useTicketContext } from "contexts/ticket-context"
import { useLocalStorage } from "hooks/utils/use-local-storage"
import { useBaseContext } from "contexts/use-base-context"
import { useRegistrationData } from "hooks/api/details/use-registration-data"
import { useTicketTypesData } from "hooks/api/tickets/use-ticket-types-data"
import { useTicketGroupsData } from "hooks/api/tickets/use-ticket-groups-data"
import { useCreateOrderMutation } from "hooks/api/order/use-create-order-mutation"

import { CreateOrderFormData, TicketType } from "types"

const SelectTickets = () => {
  const { uuid } = useParams()
  const { t } = useTranslation()
  const navigateWithParams = useNavigateWithParams()
  const { currencySymbol, isAdmin } = useBaseContext()
  const { ticketSelections } = useTicketContext()
  const { setLocalStorageData } = useLocalStorage()
  const deviceId = localStorage.getItem("device_id") || ""
  const { registrationId, bundleUUID, ticketGroup } = useRegistrationData()

  const { data: tickets, isLoading } = useTicketTypesData(
    bundleUUID,
    ticketGroup,
    isAdmin
  )
  const { data: ticketGroups, isLoading: isTicketGroupsLoading } =
    useTicketGroupsData(bundleUUID)

  const createOrder = useCreateOrderMutation({
    deviceId,
    onOrderCreated: (response) => {
      setLocalStorageData({
        timestamp: DateTime.fromISO(response.expiration).toMillis(),
        order_id: response.id,
        uuid: response.uuid,
        // Persist ticket type rank ordering for use on subsequent pages
        ticketTypeRankOrder: (sortedTickets || []).map((t) => t.id),
      })
      navigateWithParams(`/${uuid}/ticket-details`)
    },
  })

  const { register, watch, setValue } = useForm<CreateOrderFormData>({
    defaultValues: {
      tickets:
        tickets?.results?.reduce(
          (acc, ticket) => ({
            ...acc,
            [ticket.id]: { quantity: 0 },
          }),
          {}
        ) || {},
    },
  })

  const watchTickets = watch("tickets")

  const handleContinueToCheckout = () => {
    const selectedTickets = Object.entries(watchTickets)
      .filter(([_, ticketData]) => ticketData.quantity > 0)
      .map(([ticketId, ticketData]) => ({
        ticket_type_published: parseInt(ticketId),
        quantity: ticketData.quantity,
      }))

    createOrder.mutate({
      registration_website: registrationId || -1,
      tickets: selectedTickets,
    })
  }

  useEffect(() => {
    if (tickets?.results) {
      const ticketValues = tickets.results.reduce(
        (acc, ticket) => ({
          ...acc,
          [ticket.id]: {
            quantity:
              ticketSelections.find((t) => t.id === ticket.id)?.quantity || 0,
          },
        }),
        {}
      )
      setValue("tickets", ticketValues)
    }
  }, [tickets, setValue, ticketSelections])

  // Sort tickets based on groups and ranks
  const sortedTickets = useMemo(() => {
    if (!tickets?.results) return []

    // Get the ticket groups data
    const groups = ticketGroups?.results || []

    // Create a map of group id to group for faster lookups
    const groupMap = new Map(groups.map((group) => [group.id, group]))

    // Separate tickets with no group from tickets with groups
    const ticketsWithoutGroup = tickets.results
      .filter((ticket) => !ticket.ticket_group_published)
      .sort((a, b) => a.rank - b.rank)

    // Group tickets by their ticket_group_published
    const ticketsByGroup = tickets.results
      .filter((ticket) => ticket.ticket_group_published)
      .reduce((acc: { [key: number]: TicketType[] }, ticket: TicketType) => {
        const groupId = ticket.ticket_group_published!.id
        if (!acc[groupId]) {
          acc[groupId] = []
        }
        acc[groupId].push(ticket)
        return acc
      }, {})

    // Sort groups by rank and then sort tickets within each group by rank
    const sortedGroupIds = Object.keys(ticketsByGroup)
      .map(Number)
      .sort((a, b) => {
        const groupA = groupMap.get(a)
        const groupB = groupMap.get(b)
        return (groupA?.rank || 0) - (groupB?.rank || 0)
      })

    // Sort tickets within each group by rank
    for (const groupId of sortedGroupIds) {
      if (ticketsByGroup[groupId]) {
        ticketsByGroup[groupId].sort((a, b) => a.rank - b.rank)
      }
    }

    // Combine tickets without group (first) and then tickets in groups
    return [
      ...ticketsWithoutGroup,
      ...sortedGroupIds.flatMap((groupId) => ticketsByGroup[groupId] || []),
    ]
  }, [tickets?.results, ticketGroups?.results])

  // Organize tickets into groups for rendering
  const organizedTickets = useMemo(() => {
    if (!tickets?.results) return { ungrouped: [], groups: [] }

    const groups = ticketGroups?.results || []
    const groupMap = new Map(groups.map((group) => [group.id, group]))

    // Start with tickets without groups
    const result: {
      ungrouped: TicketType[]
      groups: {
        id: number
        name: string
        tickets: TicketType[]
      }[]
    } = {
      ungrouped: [],
      groups: [],
    }

    // Add ungrouped tickets first
    result.ungrouped = sortedTickets.filter(
      (ticket) => !ticket.ticket_group_published
    )

    // Group the remaining tickets
    const groupedTickets = sortedTickets.filter(
      (ticket) => ticket.ticket_group_published
    )
    const ticketGroupsMap = new Map<number, TicketType[]>()

    // Group tickets by their group id
    groupedTickets.forEach((ticket) => {
      const groupId = ticket.ticket_group_published!.id
      if (!ticketGroupsMap.has(groupId)) {
        ticketGroupsMap.set(groupId, [])
      }
      ticketGroupsMap.get(groupId)?.push(ticket)
    })

    // Create the final structure
    ticketGroupsMap.forEach((tickets: TicketType[], groupId: number) => {
      const group = groupMap.get(groupId)
      if (group) {
        result.groups.push({
          id: groupId,
          name: group.name,
          tickets,
        })
      }
    })

    return result
  }, [sortedTickets, ticketGroups?.results, tickets?.results])

  // Format price from cents to currency unit
  const formatPrice = (priceInCents: number) => {
    return (priceInCents / 100).toFixed(2)
  }

  if (isLoading || isTicketGroupsLoading) {
    return <SelectTicketsPlaceholder />
  }

  const calculateSubtotal = () => {
    return Object.entries(watchTickets).reduce(
      (total: number, [ticketId, ticketData]) => {
        const ticket = tickets?.results.find((t) => t.id === parseInt(ticketId))
        return total + (ticket?.price || 0) * ticketData.quantity
      },
      0
    )
  }

  const hasSelectedTickets = () => {
    return Object.values(watchTickets).some(
      (ticketData) => ticketData.quantity > 0
    )
  }

  return (
    <div className="container px-4 pb-8 mx-auto">
      <div className="flex flex-col gap-8 md:flex-row">
        <div className="flex-1">
          {/* Render ungrouped tickets first */}
          {organizedTickets.ungrouped.map((ticket) => {
            return (
              <TicketCard
                register={register}
                key={ticket.id}
                ticket={ticket}
                watchTickets={watchTickets}
                setValue={setValue}
              />
            )
          })}

          {/* Render grouped tickets */}
          {organizedTickets.groups.map(
            (group: { id: number; name: string; tickets: TicketType[] }) => (
              <div key={group.id} className="mb-6">
                <div className="p-3 bg-gray-50 rounded-t-lg border-b-0 border-progress_bar_background">
                  <h3 className="text-2xl font-semibold">{group.name}</h3>
                </div>
                <div className="px-3 pt-4 pb-6 bg-gray-50 rounded-b-lg border-t-0 border-progress_bar_background">
                  {group.tickets.map((ticket: TicketType) => {
                    return (
                      <TicketCard
                        register={register}
                        key={ticket.id}
                        ticket={ticket}
                        watchTickets={watchTickets}
                        setValue={setValue}
                      />
                    )
                  })}
                </div>
              </div>
            )
          )}
        </div>

        <div className="hidden w-80 md:block">
          <div className="sticky top-8 p-6 bg-white rounded-lg border border-progress_bar_background text-default_card_text">
            <h2
              className="mb-4 text-xl font-bold text-black"
              id="order-summary-heading"
            >
              {t("general.order_summary")}
            </h2>
            <div role="region" aria-labelledby="order-summary-heading">
              <ul className="p-0 m-0 list-none">
                {sortedTickets.map((ticket) => {
                  const ticketData = watchTickets?.[ticket.id]
                  if (!ticketData || ticketData.quantity === 0) return null
                  return (
                    <li key={ticket.id} className="flex justify-between mb-2">
                      <span
                        aria-label={`${ticketData.quantity} ${ticket.name} tickets`}
                      >
                        {ticketData.quantity} x {ticket.name}
                      </span>
                      <span
                        aria-label={`${currencySymbol}${formatPrice(
                          ticket.price * ticketData.quantity
                        )}`}
                      >
                        {currencySymbol}
                        {formatPrice(ticket.price * ticketData.quantity)}
                      </span>
                    </li>
                  )
                })}
              </ul>
              <div className="pt-4 mt-4 border-t border-progress_bar_background">
                <div
                  className="flex justify-between text-black"
                  aria-live="polite"
                >
                  <h3 className="mb-2 text-xl font-bold" id="subtotal-heading">
                    {t("general.subtotal")}
                  </h3>
                  <div
                    className="text-2xl font-bold"
                    aria-labelledby="subtotal-heading"
                  >
                    {currencySymbol}
                    {formatPrice(calculateSubtotal())}
                  </div>
                </div>
                <button
                  onClick={handleContinueToCheckout}
                  className={`p-2 w-full rounded-md bg-theme_accent disabled:bg-gray-300 disabled:cursor-not-allowed text-theme_accent_contrast`}
                  disabled={createOrder.isPending || !hasSelectedTickets()}
                  aria-busy={createOrder.isPending}
                  aria-label={
                    createOrder.isPending
                      ? t("general.processing")
                      : hasSelectedTickets()
                      ? `${t("selection.continue")} with ${t(
                          "general.subtotal"
                        )} of ${currencySymbol}${formatPrice(
                          calculateSubtotal()
                        )}`
                      : `${t("selection.continue")} - no tickets selected`
                  }
                >
                  {createOrder.isPending
                    ? `${t("general.processing")}...`
                    : t("selection.continue")}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="fixed right-0 bottom-0 left-0 z-50 p-4 text-black bg-white border-t md:hidden border-progress_bar_background">
          <div
            className="container flex justify-between items-center mx-auto"
            role="region"
            aria-label={t("general.order_summary")}
          >
            <div className="font-bold" aria-live="polite">
              <span className="sr-only">{t("general.subtotal")}: </span>
              {currencySymbol}
              {formatPrice(calculateSubtotal())} {t("general.total")}
            </div>
            <button
              onClick={handleContinueToCheckout}
              className="px-6 py-2 rounded-md bg-theme_accent text-theme_accent_contrast disabled:bg-gray-300 disabled:cursor-not-allowed"
              disabled={createOrder.isPending || !hasSelectedTickets()}
              aria-busy={createOrder.isPending}
              aria-label={
                createOrder.isPending
                  ? t("general.processing")
                  : hasSelectedTickets()
                  ? `${t("selection.continue")} with ${t(
                      "general.subtotal"
                    )} of ${currencySymbol}${formatPrice(calculateSubtotal())}`
                  : `${t("selection.continue")} - no tickets selected`
              }
            >
              {createOrder.isPending
                ? `${t("general.processing")}...`
                : t("selection.continue")}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export { SelectTickets }
